// Funnel v2 checkout — card fields INLINE on the page via Stripe Elements
// (OWNER DIRECTIVE 2026-06-12). There is NO Stripe-hosted Checkout, NO Embedded
// Checkout iframe, and NO redirect to collect payment.
//
// Two phases on one page:
//   order   — email + the Quick-Start Kit bump + receipt ledger. Submitting
//             creates the LTO PaymentIntent ($47 + optional $27) server-side
//             (card saved at purchase via setup_future_usage).
//   payment — a mounted <PaymentElement>. "Pay" confirms in-page with
//             stripe.confirmPayment({ redirect: 'if_required' }); on success we
//             navigate to /oto with SPA routing (no full page load). 3DS, if a
//             card needs it, is an inline modal — still no navigation.

import { CLAIM_SECRET_STORAGE_KEY } from '../../lib/funnel-api';
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { BumpToggle, type Bump } from './BumpToggle';
import { LedgerRow, PrimaryCtaButton } from './funnel/atoms';
import { FunnelPriceNotice } from './funnel/FunnelPriceNotice';
import { requestLoginLink, startPaymentIntent } from '../../lib/funnel-api';
import { formatUsd, useFunnelPriceCatalog } from '../../lib/funnel-prices';
import { getStripe, isStripeAvailable } from '../../lib/stripe-elements';

/**
 * Issue #1195 — LAUNCH BLOCKER. Both reassurance lines on this page used to
 * read "We never store your card." That was FALSE: the LTO PaymentIntent is
 * created with `setup_future_usage: 'off_session'` against an attached Stripe
 * Customer (apps/server/src/services/roleforge-checkout.ts), so the card is
 * saved at the moment of purchase — unconditionally, whether or not the buyer
 * ever accepts the free OTO. Stripe requires the customer be TOLD the payment
 * method is being saved, and agree, BEFORE it is collected.
 *
 * This string is the disclosure. It is rendered under the order form and again
 * ABOVE the PaymentElement, so it is on screen before any card digit is typed.
 * The "no subscription on this page" claim elsewhere on the page is true and
 * stays. No urgency, no scarcity — see docs/marketing/copy-rules.md.
 */
export const CARD_ON_FILE_DISCLOSURE =
  'Card details are entered securely on this page and handled by Stripe. Your card is saved on file so the free upgrade on the next page takes one click. Nothing beyond today’s total is charged unless you choose it.';

function CardOnFileDisclosure() {
  return (
    <p
      data-testid="card-on-file-disclosure"
      className="flex items-start justify-center gap-2 text-xs leading-5 text-slate-500"
    >
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-teal" aria-hidden="true" />
      <span>{CARD_ON_FILE_DISCLOSURE}</span>
    </p>
  );
}

/**
 * Issue #1321 defect 2. This key used to persist the $27 bump to localStorage
 * and was NEVER cleared — not even after a successful purchase. A returning
 * buyer therefore landed on /checkout with a PAID add-on already ticked and
 * already in the total, which jumped $47 → $74 after first paint. Pre-ticked
 * paid add-ons are prohibited for buyers in EU consumer-law scope, and a
 * silently re-added charge is indefensible anywhere.
 *
 * The bump is no longer restored from storage at all: every visit starts
 * unticked, and the buyer's choice lives in component state for the life of the
 * page (Edit order round-trips keep it because the component stays mounted).
 * The key survives here only so a successful payment can PURGE the value left
 * behind in real buyers' browsers by the shipped version.
 */
const BUMP_STORAGE_KEY = 'roleforge-quick-start-kit-selected';

/**
 * Issue #1321 defect 2 — clear the legacy pre-tick. Called on the success path.
 */
function clearPersistedBump() {
  try {
    localStorage.removeItem(BUMP_STORAGE_KEY);
  } catch {
    // Storage-disabled browsers never had the key to begin with.
  }
}

/**
 * Issue #1321 — the refusal below is a 100% checkout outage by design, and the
 * mismatch can be a stale served catalog or a race between revalidation and
 * PaymentIntent creation. Without a signal it is discovered by buyers, one
 * lost sale at a time. There is no analytics or error-reporting transport in
 * this app today, so this structured console error reaches the browser console,
 * session replay and any future reporter, and is greppable.
 *
 * The served catalog now supplies the display quote at runtime (issue #1358),
 * while the immediate refresh and server-amount comparison remain safety nets
 * for stale data and races.
 */
function reportPriceMismatch(serverCents: number, quotedCents: number) {
  try {
    console.error(
      '[checkout] served-catalog price race or mismatch — refusing to collect a card. The PaymentIntent amount differs from the currently quoted served catalog; revalidate the catalog and retry.',
      { serverCents, quotedCents },
    );
  } catch {
    // Never let telemetry break a checkout.
  }
}

const ELEMENTS_APPEARANCE = {
  theme: 'night' as const,
  variables: {
    colorPrimary: '#FE5963',
    colorBackground: '#090c12',
    colorText: '#e2e8f0',
    borderRadius: '6px',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
};

export function CheckoutForm() {
  const prices = useFunnelPriceCatalog();
  const [email, setEmail] = useState('');
  const [bumpSelected, setBumpSelected] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Issue #983 item 2: empty-submit used to show only a red border (native
  // :invalid styling), no accessible error text.
  const [emailError, setEmailError] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  // Issue #1321: the amount the server actually put on the PaymentIntent. This,
  // never a local constant, is what the payment phase displays.
  const [serverAmountCents, setServerAmountCents] = useState<number | null>(null);
  // Issue #1321: set when the server's amount disagrees with the total this page
  // quoted. No card is collected in that state.
  const [priceChanged, setPriceChanged] = useState<{
    serverCents: number;
    quotedCents: number;
  } | null>(null);
  const [subscriberAppUrl, setSubscriberAppUrl] = useState<string | null>(null);
  const [subscriberEmail, setSubscriberEmail] = useState<string | null>(null);
  // Issue #1196 — this buyer already paid and their first Blueprint is still
  // in flight. They must not be charged a second time.
  const [inProgressAppUrl, setInProgressAppUrl] = useState<string | null>(null);
  const [inProgressEmail, setInProgressEmail] = useState<string | null>(null);

  // Issue #1220 — the phase swap used to replace the whole form, including the
  // button the buyer just pressed, with nothing moving focus or announcing the
  // change: focus fell to the document and a keyboard or screen-reader buyer
  // had to Tab blindly from the top of the page to find out a card form
  // appeared. Each phase now owns a focusable region, focused on the swap.
  const orderRegionRef = useRef<HTMLFormElement>(null);
  const returningToOrderRef = useRef(false);

  const quickStartKit = useMemo<Bump>(() => ({
    id: 'quick_start_kit',
    name: 'Add the AI Workforce Quick-Start Kit',
    price_cents: prices.quickStartKitCents,
    description: 'The preparation layer your Blueprint builds on — ready before your first session.',
    includes: [
      'Business Context Brief template',
      'Voice and Tone capture worksheet',
      'Process Capture framework with three worked examples',
      'Your First Session walkthrough video',
      'Trust Ladder for graduating AI workers safely',
    ],
  }), [prices.quickStartKitCents]);
  const total = useMemo(
    () => prices.blueprintCents + (bumpSelected ? quickStartKit.price_cents : 0),
    [bumpSelected, prices.blueprintCents, quickStartKit.price_cents],
  );
  const quotedLabel = formatUsd(total);

  function editOrder() {
    returningToOrderRef.current = true;
    setClientSecret(null);
    setPaymentIntentId(null);
    setServerAmountCents(null);
    setPriceChanged(null);
    setError(null);
  }

  // Issue #1220: only ever moves focus on a RETURN to the order phase — never
  // on first paint, which would yank focus away from a buyer who just landed.
  useEffect(() => {
    if (clientSecret || subscriberAppUrl || inProgressAppUrl || priceChanged) return;
    if (!returningToOrderRef.current) return;
    returningToOrderRef.current = false;
    orderRegionRef.current?.focus();
  }, [clientSecret, subscriberAppUrl, inProgressAppUrl, priceChanged]);

  async function handleContinue(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setEmailError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Enter your email to continue.');
      return;
    }

    if (!isStripeAvailable()) {
      setError('Payment is temporarily unavailable. Give it a minute and try again — nothing has been charged.');
      return;
    }
    if (!prices.canCheckout) {
      setError('Price check is temporarily unavailable. Refresh this page before continuing — nothing has been charged.');
      return;
    }

    setSubmitting(true);
    try {
      // Revalidate immediately before creating the PaymentIntent. A page can
      // remain open beyond the ten-minute acceptance window, and a cached
      // display must never become a payment quote just because it was live on
      // first paint.
      const latestPrices = await prices.refresh();
      const latestTotal = latestPrices.blueprintCents +
        (bumpSelected ? latestPrices.quickStartKitCents : 0);
      if (!latestPrices.canCheckout) {
        setError('Price check is temporarily unavailable. Refresh this page before continuing — nothing has been charged.');
        return;
      }
      if (latestTotal !== total) {
        setError('The price was updated. Review the current order total before continuing.');
        return;
      }
      const bumps = bumpSelected ? ['bump_quick_start_kit'] : [];
      const result = await startPaymentIntent({ email: trimmedEmail, bumps });
      if (result.status === 'subscriber') {
        setSubscriberEmail(trimmedEmail);
        setSubscriberAppUrl(result.appUrl);
        return;
      }
      // Issue #1196: the server declined to create a PaymentIntent because
      // this buyer already paid and their Blueprint is still in progress.
      if (result.status === 'blueprint_in_progress') {
        setInProgressEmail(trimmedEmail);
        setInProgressAppUrl(result.appUrl);
        return;
      }
      // Issue #1321 defect 1. The server decided the price; this page only
      // quoted it. If the two disagree, the buyer is about to authorize a number
      // they were never shown — so refuse to mount the card fields and say so.
      // Undercharging is not a safe direction either: it means the ledger,
      // both CTAs and the receipt are all telling a different story.
      if (result.amountCents !== latestTotal) {
        reportPriceMismatch(result.amountCents, latestTotal);
        setPriceChanged({ serverCents: result.amountCents, quotedCents: latestTotal });
        return;
      }
      setServerAmountCents(result.amountCents);
      setClientSecret(result.clientSecret);
      setPaymentIntentId(result.paymentIntentId);
    } catch {
      setError(
        'Checkout is temporarily unavailable. Give it a minute and try again — your card has not been charged.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  // Issue #983 item 4: the "already a subscriber" panel named no account and
  // offered no escape if the buyer typed the wrong email.
  function notMe() {
    returningToOrderRef.current = true;
    setSubscriberAppUrl(null);
    setSubscriberEmail(null);
    setInProgressAppUrl(null);
    setInProgressEmail(null);
    setEmail('');
  }

  const stripePromise = getStripe();

  if (subscriberAppUrl) {
    return (
      <SubscriberPanel appUrl={subscriberAppUrl} email={subscriberEmail} onNotMe={notMe} />
    );
  }

  if (inProgressAppUrl) {
    return (
      <BlueprintInProgressPanel
        appUrl={inProgressAppUrl}
        email={inProgressEmail}
        onNotMe={notMe}
      />
    );
  }

  // Issue #1321: no card fields for a total we cannot honestly state.
  if (priceChanged) {
    return (
      <PriceChangedPanel
        serverCents={priceChanged.serverCents}
        quotedCents={priceChanged.quotedCents}
        onEditOrder={editOrder}
      />
    );
  }

  if (clientSecret && paymentIntentId && stripePromise && serverAmountCents !== null) {
    return (
      <Elements stripe={stripePromise} options={{ clientSecret, appearance: ELEMENTS_APPEARANCE }}>
        {/* Issue #1321: the amount rendered here is the server's, not a local
            constant — this is the number the buyer authorizes. */}
        <PaymentPhase amountCents={serverAmountCents} onEditOrder={editOrder} />
      </Elements>
    );
  }

  return (
    <form
      onSubmit={handleContinue}
      className="space-y-6 focus:outline-none"
      data-testid="order-phase"
      ref={orderRegionRef}
      tabIndex={-1}
      aria-label="Your order"
    >
      <label htmlFor="checkout-email" className="block">
        <span className="mb-2 block text-sm font-semibold text-white">Email for your Blueprint access</span>
        <input
          type="email"
          id="checkout-email"
          name="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          autoComplete="email"
          aria-invalid={emailError ? true : undefined}
          aria-describedby={emailError ? 'checkout-email-error' : undefined}
          className="w-full rounded-md border border-white/15 bg-[#080a0f] px-4 py-3.5 text-white placeholder-slate-600 transition focus:border-brand-coral/70 focus:outline-none focus:ring-2 focus:ring-brand-coral/35"
          placeholder="you@company.com"
        />
      </label>
      {emailError ? (
        <p id="checkout-email-error" role="alert" className="text-sm text-brand-coral">
          {emailError}
        </p>
      ) : null}

      {/* Issue #1321 defect 2: `selected` comes from component state only. A
          paid add-on is never restored from storage — see BUMP_STORAGE_KEY. */}
      <BumpToggle
        bump={quickStartKit}
        selected={bumpSelected}
        onChange={setBumpSelected}
        disabled={submitting}
      />

      <div className="rounded-md border border-white/10 bg-[#0b0f16] px-5 py-2">
        <LedgerRow
          label="AI Workforce Blueprint"
          sub="Guided sessions plus your written, deployable plan"
          amount={formatUsd(prices.blueprintCents)}
        />
        {bumpSelected ? (
          <LedgerRow
            label="AI Workforce Quick-Start Kit"
            sub="Added to your order"
            amount={formatUsd(quickStartKit.price_cents)}
          />
        ) : null}
        <div className="flex items-baseline justify-between pt-3 pb-1">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">Total today</span>
          <span data-testid="checkout-total" className="font-display text-2xl font-semibold tabular-nums text-white">
            {quotedLabel}
          </span>
        </div>
        <p className="mb-2 text-right text-xs text-slate-500">One-time. No subscription on this page.</p>
      </div>

      <FunnelPriceNotice snapshot={prices} />

      <PrimaryCtaButton type="submit" disabled={submitting || !prices.canCheckout} className="w-full">
        {submitting ? 'Preparing secure payment…' : `Continue to payment — ${quotedLabel}`}
      </PrimaryCtaButton>

      {error ? (
        <p
          role="alert"
          className="rounded-md border border-brand-coral/25 bg-brand-coral/10 px-4 py-3 text-center text-sm leading-6 text-brand-coral"
        >
          {error}
        </p>
      ) : null}

      <CardOnFileDisclosure />
    </form>
  );
}

// ---------------------------------------------------------------------------
// Terminal panels. Both are separate components so their focus effect runs on
// MOUNT — which is exactly the moment the phase swaps (issue #1220).
// ---------------------------------------------------------------------------

function useFocusOnMount() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return ref;
}

const OPEN_ROLEFORGE_CLASSES =
  'mt-6 inline-flex items-center justify-center gap-2.5 rounded-md bg-brand-coral px-7 py-4 font-display text-[15px] font-semibold tracking-tight text-white shadow-[0_18px_48px_rgba(254,89,99,0.32)] transition hover:-translate-y-0.5 hover:bg-[#ff6b72] focus:outline-none focus:ring-2 focus:ring-brand-coral/70 focus:ring-offset-2 focus:ring-offset-[#080a0f]';

function SubscriberPanel({
  appUrl,
  email,
  onNotMe,
}: {
  appUrl: string;
  email: string | null;
  onNotMe: () => void;
}) {
  const regionRef = useFocusOnMount();
  return (
    <div
      ref={regionRef}
      tabIndex={-1}
      data-testid="subscriber-panel"
      className="rounded-md border border-white/10 bg-[#090c12] px-5 py-6 text-center focus:outline-none"
    >
      <h3 className="font-display text-lg font-semibold text-white">You&apos;re already a subscriber.</h3>
      <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-400">
        {email ? (
          <>
            <span className="font-medium text-slate-300">{email}</span> is on an active plan.
          </>
        ) : null}{' '}
        New AI Workforce Blueprints are included in your plan — no need to buy another. Open RoleForge to
        start a blueprint, or create a new organization from your VibeControl dashboard for a separate
        business.
      </p>
      <a href={appUrl} className={OPEN_ROLEFORGE_CLASSES}>
        Open RoleForge
      </a>
      <p className="mt-4">
        <button
          type="button"
          onClick={onNotMe}
          className="text-sm text-slate-500 underline transition hover:text-slate-300"
        >
          Not you? Use a different email
        </button>
      </p>
    </div>
  );
}

/**
 * Issue #1196. This buyer already paid; their first Blueprint is still in
 * progress. Before this panel existed they were simply charged again — and the
 * webhook then deliberately did nothing with the money: no reset, no access
 * email, no record. They are almost always here because they lost the access
 * email or the payment UI reported an error after the charge succeeded, so the
 * action offered is a fresh sign-in link, not a second purchase.
 */
function BlueprintInProgressPanel({
  appUrl,
  email,
  onNotMe,
}: {
  appUrl: string;
  email: string | null;
  onNotMe: () => void;
}) {
  const regionRef = useFocusOnMount();
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function resendLink() {
    if (!email || sending) return;
    setSending(true);
    setNotice(null);
    const result = await requestLoginLink({ email });
    if (result.status === 'sent') {
      setNotice(`Sign-in link sent to ${email}. It can take a minute to arrive.`);
    } else if (result.status === 'rate_limited') {
      setNotice(
        `A link was just sent to ${email}. Check your inbox, or request another in ${result.retryAfterSeconds} seconds.`,
      );
    } else {
      setNotice('That link could not be sent right now. Try again in a moment, or use Open RoleForge below.');
    }
    setSending(false);
  }

  return (
    <div
      ref={regionRef}
      tabIndex={-1}
      data-testid="blueprint-in-progress-panel"
      className="rounded-md border border-white/10 bg-[#090c12] px-5 py-6 text-center focus:outline-none"
    >
      <h3 className="font-display text-lg font-semibold text-white">
        You already have a Blueprint in progress.
      </h3>
      <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-400">
        {email ? (
          <>
            <span className="font-medium text-slate-300">{email}</span> has already paid for a Blueprint,
            and it is still open where you left it.{' '}
          </>
        ) : null}
        There is nothing more to buy — we have not charged you again. Send yourself a fresh sign-in link
        and pick it back up.
      </p>

      <div className="mx-auto mt-6 grid max-w-xs gap-3">
        {email ? (
          <PrimaryCtaButton type="button" onClick={resendLink} disabled={sending} className="w-full">
            {sending ? 'Sending your sign-in link…' : 'Email me my sign-in link'}
          </PrimaryCtaButton>
        ) : null}
        <a
          href={appUrl}
          className="inline-flex w-full items-center justify-center rounded-md border border-white/12 bg-white/[0.03] px-6 py-3.5 text-sm font-medium text-slate-300 transition hover:border-white/25 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30"
        >
          Open RoleForge
        </a>
      </div>

      {notice ? (
        <p role="status" className="mt-4 text-sm leading-6 text-slate-400">
          {notice}
        </p>
      ) : null}

      <p className="mt-4">
        <button
          type="button"
          onClick={onNotMe}
          className="text-sm text-slate-500 underline transition hover:text-slate-300"
        >
          Not you? Use a different email
        </button>
      </p>
    </div>
  );
}

/**
 * Issue #1321 defect 1. The server's amount for this order is not the total this
 * page quoted. Something changed `roleforge_funnel_skus.amount_cents` under a
 * page that was already loaded (or the deployed page is behind the SKU table).
 *
 * The buyer is told the real number and NO card is collected. Charging in this
 * state is precisely the defect: the buyer would authorize a figure they never
 * saw. A reload picks up whatever the current page and prices are.
 */
function PriceChangedPanel({
  serverCents,
  quotedCents,
  onEditOrder,
}: {
  serverCents: number;
  quotedCents: number;
  onEditOrder: () => void;
}) {
  const regionRef = useFocusOnMount();
  return (
    <div
      ref={regionRef}
      tabIndex={-1}
      data-testid="price-changed-panel"
      role="alert"
      className="rounded-md border border-brand-coral/25 bg-[#090c12] px-5 py-6 text-center focus:outline-none"
    >
      <h3 className="font-display text-lg font-semibold text-white">
        The price for this order changed.
      </h3>
      <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-400">
        Today&apos;s total for this order is{' '}
        <span data-testid="price-changed-server-total" className="font-medium text-white">
          {formatUsd(serverCents)}
        </span>
        , not the {formatUsd(quotedCents)} this page showed. We will not take a card for a total you
        have not seen, so nothing has been charged and no card details were requested.
      </p>
      <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-slate-400">
        Reload this page to see the current price, then continue if it still looks right to you.
      </p>
      <div className="mx-auto mt-6 grid max-w-xs gap-3">
        <PrimaryCtaButton
          type="button"
          onClick={() => window.location.reload()}
          className="w-full"
        >
          Reload with the current price
        </PrimaryCtaButton>
        <button
          type="button"
          onClick={onEditOrder}
          className="text-sm text-slate-500 underline transition hover:text-slate-300"
        >
          Back to your order
        </button>
      </div>
    </div>
  );
}

interface PaymentPhaseProps {
  /** Issue #1321: the server's PaymentIntent amount, in cents. */
  amountCents: number;
  onEditOrder: () => void;
}

/**
 * Issue #1321 defect 3. The PaymentElement can fail to mount entirely — a
 * tracker blocker, a corporate proxy or an extension blocking js.stripe.com is
 * the common case. Before this timeout existed, `elementReady` simply stayed
 * false forever: an aria-hidden pulsing skeleton and a permanently disabled Pay
 * button, with no error, no retry and no explanation. Stripe's own mount takes
 * ~8-10s on a slow connection, so the window is generous.
 */
const ELEMENT_MOUNT_TIMEOUT_MS = 15_000;

function PaymentPhase({ amountCents, onEditOrder }: PaymentPhaseProps) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Issue #983 item 3: the PaymentElement iframe takes ~8-10s to mount with
  // no loading state — a black void under a live Pay button. Show a skeleton
  // until Stripe reports the element is ready.
  const [elementReady, setElementReady] = useState(false);
  // Issue #1321 defect 3: the card field never loaded. `mountAttempt` keys the
  // PaymentElement so "try again" is a real remount, not a no-op.
  const [mountFailed, setMountFailed] = useState(false);
  const [mountAttempt, setMountAttempt] = useState(0);
  const totalLabel = formatUsd(amountCents);
  // Issue #1220: this component mounts exactly when the order→payment swap
  // happens, so focusing on mount is the swap handler.
  const regionRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    regionRef.current?.focus();
  }, []);

  // Issue #1321 defect 3: `onLoadError` covers the failures Stripe reports;
  // this timeout covers the ones it never gets to report — a blocked
  // js.stripe.com means neither onReady nor onLoadError ever fires.
  useEffect(() => {
    if (elementReady || mountFailed) return;
    const timer = setTimeout(() => setMountFailed(true), ELEMENT_MOUNT_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [elementReady, mountFailed, mountAttempt]);

  /**
   * A LATE mount is a success, not a failure. The timeout above is a heuristic:
   * on a congested mobile connection Stripe can mount well past 15s, and the
   * PaymentElement is never unmounted while we wait. If we treated a late
   * `onReady` as anything other than recovery, the buyer would end up looking at
   * a working, typeable card field underneath an alert claiming it could not be
   * loaded — with no Pay button, and a retry that loses their typed digits and
   * re-runs the same race. "Slow" must never become "cannot buy".
   *
   * `cardFieldUnavailable` is belt-and-braces for the same rule: the failure UI
   * can never render over a card field that is actually live.
   */
  function handleElementReady() {
    setElementReady(true);
    setMountFailed(false);
  }
  const cardFieldUnavailable = mountFailed && !elementReady;

  function retryCardField() {
    // Never remount Elements under a running confirmation. `pay()` also guards
    // on `paying`, but a remount mid-confirm is not a state worth reasoning
    // about on a money path.
    if (paying) return;
    setMountFailed(false);
    setElementReady(false);
    setError(null);
    setMountAttempt((n) => n + 1);
  }

  async function pay(e: FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || paying) return;
    setPaying(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message ?? 'Please check your card details and try again.');
      setPaying(false);
      return;
    }

    // The Elements provider already holds the PaymentIntent clientSecret, so we
    // confirm via `elements` (passing clientSecret here too would throw). With
    // redirect 'if_required' a successful card charge returns inline (3DS is an
    // inline modal, no navigation); return_url is only used if Stripe ever
    // needs a redirect, which our card-only PaymentIntent avoids.
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/oto` },
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(
        confirmError.message ?? 'That card could not be charged. Try again or use a different card — nothing has been charged.',
      );
      setPaying(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Issue #1004: the thank-you page proves it is the paying device by
      // presenting this PaymentIntent's client_secret, which Stripe issues only
      // to the browser that confirmed the payment. Keep it in sessionStorage —
      // never in the URL, where it would leak through Referer headers, history,
      // analytics and shared links exactly like the id it replaces.
      // Issue #1321 defect 2: the order is paid, so the persisted bump choice
      // left behind by the shipped version must not survive to pre-tick a paid
      // add-on on this buyer's next visit.
      clearPersistedBump();
      try {
        sessionStorage.setItem(CLAIM_SECRET_STORAGE_KEY, paymentIntent.client_secret ?? '');
      } catch {
        // Private-mode or storage-disabled browsers simply fall back to the
        // emailed sign-in link; the claim shortcut is a convenience, not a
        // requirement.
      }
      // Client-side SPA navigation to the OTO — no full page load.
      navigate(`/oto?payment_intent_id=${encodeURIComponent(paymentIntent.id)}`);
      return;
    }

    setError('Your payment is still processing. Give it a moment and refresh — nothing extra will be charged.');
    setPaying(false);
  }

  return (
    <form
      onSubmit={pay}
      className="space-y-4 focus:outline-none"
      data-testid="inline-payment"
      ref={regionRef}
      tabIndex={-1}
      aria-labelledby="checkout-payment-heading"
    >
      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={onEditOrder}
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 transition hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Edit order
        </button>
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
          Total today&nbsp;
          <span className="text-white">{totalLabel}</span>
        </p>
      </div>

      {/* Issue #1220: names the new phase for the buyer who just pressed
          "Continue to payment" and would otherwise hear nothing at all. */}
      <h3
        id="checkout-payment-heading"
        role="status"
        className="font-display text-base font-semibold text-white"
      >
        Order confirmed — enter your card details
      </h3>

      {/* Issue #1195: the disclosure sits ABOVE the card fields, because
          Stripe requires the buyer be told the card will be saved BEFORE it
          is collected — not in fine print underneath. */}
      <CardOnFileDisclosure />

      {/* #1307: `aria-busy` on the container that actually wraps the card
          fields, so assistive tech knows the region is still loading rather
          than empty. The skeleton itself stays `aria-hidden` — it is decoration.
          The pulse is `motion-safe:` so a buyer who has asked their OS for
          reduced motion gets a static placeholder instead of a throbbing one
          on the payment step. */}
      <div className="relative" aria-busy={!elementReady && !cardFieldUnavailable}>
        {/* Issue #1321 defect 3: the skeleton stops pulsing once we have given
            up — an animation that never resolves reads as "still working". */}
        {!elementReady && !cardFieldUnavailable ? (
          <div
            aria-hidden="true"
            className="absolute inset-0 z-10 motion-safe:animate-pulse space-y-3 rounded-md border border-white/10 bg-[#0b0f16] p-4"
          >
            <div className="h-9 rounded bg-white/[0.06]" />
            <div className="h-9 rounded bg-white/[0.06]" />
            <div className="h-9 w-2/3 rounded bg-white/[0.06]" />
          </div>
        ) : null}
        <PaymentElement
          key={mountAttempt}
          options={{ layout: 'tabs' }}
          onReady={handleElementReady}
          onLoadError={() => setMountFailed(true)}
        />
      </div>

      {cardFieldUnavailable ? (
        <div data-testid="card-field-unavailable" className="space-y-3">
          <p
            role="alert"
            className="rounded-md border border-brand-coral/25 bg-brand-coral/10 px-4 py-3 text-center text-sm leading-6 text-brand-coral"
          >
            We could not load the secure card field. A tracker blocker, browser extension or network
            filter blocking Stripe is the usual cause. Nothing has been charged.
          </p>
          <PrimaryCtaButton type="button" onClick={retryCardField} className="w-full">
            Try loading the card field again
          </PrimaryCtaButton>
          <p className="text-center text-xs leading-5 text-slate-500">
            If it still does not load, allow js.stripe.com or open this page in another browser. You
            have not been charged.
          </p>
        </div>
      ) : (
        <PrimaryCtaButton
          type="submit"
          disabled={paying || !stripe || !elementReady}
          className="w-full"
        >
          {paying ? 'Confirming payment…' : `Pay ${totalLabel} and start your Blueprint`}
        </PrimaryCtaButton>
      )}

      {error ? (
        <p
          role="alert"
          className="rounded-md border border-brand-coral/25 bg-brand-coral/10 px-4 py-3 text-center text-sm leading-6 text-brand-coral"
        >
          {error}
        </p>
      ) : null}

    </form>
  );
}
